import { baseFontSize, pxToRem } from '@fluentsms/agentnet-web-components';
import { grey } from '@material-ui/core/colors';
import { Overrides } from '@material-ui/core/styles/overrides';
import breakpoints from '../breakpoints';
import palette from '../palette/palette';
import spacing from '../spacing';
import typography from '../typography';

declare module '@material-ui/core/styles/overrides' {
  interface ComponentNameToClassKey {
    MuiAlert: any;
  }
}

const overrides: Overrides = {
  MuiCssBaseline: {
    '@global': {
      body: {
        fontFamily: ['Inter', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
        fontWeight: 400,
        fontSize: baseFontSize,
      },
    },
  },

  MuiButton: {
    outlined: {
      backgroundColor: 'transparent',
      fontSize: pxToRem(14),
      lineHeight: pxToRem(24),

      '&:hover, &:focus': {
        borderColor: palette.primary.dark,
        backgroundColor: palette.primary.light,
        boxShadow: 'none',
      },

      '&:disabled': {
        borderColor: palette.action.disabledBackground,
        color: palette.action.disabled,
      },

      '&.MuiButton-outlinedSecondary': {
        '&:disabled': {
          borderColor: `${palette.secondary.main}26`,
          color: `${palette.secondary.main}66`,
        },
      },

      '&.outlinedError': {
        backgroundColor: 'transparent',
        borderColor: palette.error.main,
        color: palette.error.main,

        '&:hover, &:focus': {
          backgroundColor: 'transparent',
          borderColor: palette.error.dark,
          color: palette.error.dark,
        },
        '&:disabled': {
          borderColor: `${palette.error.main}26`,
          color: `${palette.error.main}66`,
        },
      },

      '&.outlinedInfo': {
        backgroundColor: 'transparent',
        borderColor: palette.info.main,
        color: palette.info.main,

        '&:hover, &:focus': {
          backgroundColor: 'transparent',
          borderColor: palette.info.dark,
          color: palette.info.dark,
        },
        '&:disabled': {
          borderColor: `${palette.info.main}26`,
          color: `${palette.info.main}66`,
        },
      },

      '&.outlinedWarning': {
        backgroundColor: 'transparent',
        borderColor: palette.warning.main,
        color: palette.warning.main,

        '&:hover, &:focus': {
          backgroundColor: 'transparent',
          borderColor: palette.warning.dark,
          color: palette.warning.dark,
        },
        '&:disabled': {
          borderColor: `${palette.warning.main}26`,
          color: `${palette.warning.main}66`,
        },
      },

      '&.outlinedSuccess': {
        backgroundColor: 'transparent',
        borderColor: palette.success.main,
        color: palette.success.main,

        '&:hover, &:focus': {
          backgroundColor: 'transparent',
          borderColor: palette.success.dark,
          color: palette.success.dark,
        },
        '&:disabled': {
          borderColor: `${palette.success.main}26`,
          color: `${palette.success.main}66`,
        },
      },
    },
    outlinedSizeLarge: {
      fontSize: pxToRem(15),
      lineHeight: pxToRem(26),
      padding: spacing(1, 2.75),
    },
    outlinedSizeSmall: {
      fontSize: pxToRem(13),
      lineHeight: pxToRem(22),
      padding: spacing(0.5, 1.25),
    },
  },

  //SVG ICON
  MuiSvgIcon: {
    root: {
      fontSize: pxToRem(16),
    },
    fontSizeLarge: {
      fontSize: pxToRem(24),
    },
  },

  //FORM INPUTS
  MuiSelect: {
    select: {
      textAlign: 'left',
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    selectMenu: {
      height: '0',
    },
    iconOpen: {
      transform: 'rotate(180deg)',
    },
    icon: {
      color: palette.actionSecondary.active,
    },
  },
  MuiInputBase: {
    root: {
      '&$disabled': {
        background: palette.actionSecondary.disabledBackground,
        color: palette.actionSecondary.disabled,
      },
    },
    input: {
      ...typography.body2,
      color: palette.text.primary,
      fontSize: pxToRem(14),
      fontWeight: 400,
      fontFamily: ['Inter', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
      position: 'relative',
      padding: '8px 0 10px',
      '&$disabled': {
        background: grey[50],
        color: palette.text.disabled,
        borderColor: palette.statesSecondary.disabledBorder,
      },
    },
  },
  MuiOutlinedInput: {
    root: {
      overflow: 'hidden',
      '&$disabled': {
        '& fieldset.MuiOutlinedInput-notchedOutline': {
          borderColor: palette.statesSecondary.disabledBorder,
        },
      },
      '&$focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: '1px',
          borderColor: palette.primary.main,
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        transition: '.3s all cubic-bezier(0.420, 0.000, 0.580, 1.000)',
      },
      '&:hover': {
        '& .MuiOutlinedInput-notchedOutline': {
          transition: '.2s all cubic-bezier(0.420, 0.000, 0.580, 1.000)',
          borderColor: palette.primary.dark,
        },
      },
      '& legend': {
        maxWidth: '1000px',
      },
      '&$error $notchedOutline': {
        borderColor: `${palette.error.main} !important`,
      },
      '&.MuiOutlinedInput-adornedEnd': {
        paddingRight: '8px',
        '&.Mui-disabled': {
          background: palette.statesSecondary.disabledBackground,
        },
      },
      '&.MuiOutlinedInput-adornedStart': {
        '&.Mui-disabled': {
          background: palette.statesSecondary.disabledBackground,
        },
      },
    },
    input: {
      padding: '10px 12px',
      color: palette.text.primary,
      fontWeight: 400,
      borderColor: '#c4c4c4',
    },
    inputMarginDense: {
      paddingTop: pxToRem(15),
      paddingBottom: pxToRem(15),
    },
  },
  MuiInputLabel: {
    formControl: {
      fontSize: pxToRem(13),
    },
    filled: {
      transform: ' translate(12px, 8px) scale(1)',
      fontSize: pxToRem(13),
    },
    outlined: {
      fontSize: pxToRem(16),
      fontWeight: 500,
      letterSpacing: '0.15px',
      color: palette.text.secondary,
      '&.MuiInputLabel-shrink': {
        fontSize: pxToRem(13),
        transform: `translate(14px, -6px)`,
        backgroundColor: 'white',
        '&.Mui-required': {
          display: 'flex',
          flexDirection: 'row-reverse',
          transform: `translate(8px, -6px)`,
          '&.Mui-disabled .MuiInputLabel-asterisk': {
            display: 'none',
          },
        },
      },
    },
  },
  MuiFormHelperText: {
    root: {
      fontSize: pxToRem(12),
      color: palette.text.secondary,
      '&$focused': {
        color: palette.primary.main,
      },
    },
  },
  MuiFilledInput: {
    input: {
      padding: pxToRem(27, 12, 10, 10),
    },
    inputMultiline: {
      padding: pxToRem(30, 0),
    },
  },
  MuiFormLabel: {
    asterisk: {
      color: palette.error.dark,
      fontSize: pxToRem(14),
      fontWeight: 600,
      '&$error': {
        color: palette.error.dark,
      },
    },
  },
  MuiInputAdornment: {
    positionEnd: {
      marginLeft: 'auto',
    },
  },

  //SWITCH
  MuiSwitch: {
    root: {
      '&$checked': {
        color: palette.primary.main,
      },
    },
    colorSecondary: {
      '&$checked': {
        color: palette.primary.main,
      },
    },
    track: {
      opacity: 0.38,
      backgroundColor: '#000',
      '$checked$checked + &': {
        opacity: 0.7,
        backgroundColor: palette.primary.main,
      },
    },
  },

  //CHECKBOX
  MuiCheckbox: {
    root: {
      padding: `2px 9px`,
      '&.MuiCheckbox-root:hover': {
        backgroundColor: 'transparent',
      },
      '&.MuiCheckbox-root.Mui-checked:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  //MENU
  MuiMenu: {
    paper: {},
  },
  //MENU ITEM
  MuiMenuItem: {
    root: {
      fontSize: pxToRem(16),
      color: palette.text.primary,
    },
  },
  MuiListItem: {
    button: {
      '&:hover': {
        backgroundColor: palette.action.hover,
      },
    },
  },
  MuiPopover: {
    paper: {
      top: '155px',
    },
  },

  //TEXT INPUT
  MuiInput: {
    formControl: {
      'label + &': {
        marginTop: 19,
      },
    },
    underline: {
      '&.MuiInput-formControl:before': {
        borderBottomStyle: 'none',
      },
      '& .MuiInputAdornment-root': {
        marginBottom: spacing(0.375),
        '& .MuiIconButton-root': {
          padding: 3,
          fontSize: spacing(2.25),
          marginBottom: spacing(0.5),
        },
      },
      '&:hover:not($disabled):before': {
        borderBottomColor: 'none',
      },
    },
  },

  //DROPDOWN
  MuiFormControl: {
    root: {
      backgroundColor: palette.background.paper,
    },
  },

  //FORMCONTROL FOR SWITCHES
  MuiFormControlLabel: {
    root: {
      marginLeft: 0,
    },
  },

  //TYPOGRAPHY
  MuiTypography: {
    root: {
      padding: 0,
    },
    h5: {
      padding: 0,
    },
  },

  //AUTOCOMPLETE
  MuiAutocomplete: {
    root: {
      '&.MuiAutocomplete-hasClearIcon,.MuiAutocomplete-hasPopupIcon': {
        height: '97.5%',
      },
    },
    popupIndicator: {
      '& div': {
        padding: 0,
        marginRight: 0,
      },
    },
    inputRoot: {
      padding: '0px',
      '&[class*="MuiOutlinedInput-root"]': {
        padding: '0px',
        '& .MuiAutocomplete-input': {
          padding: '10px 12px',
        },
        '& .MuiAutocomplete-input:first-child': {
          paddingLeft: '12px',
        },
      },
    },
    noOptions: {
      padding: `16px 24px`,
      fontSize: pxToRem(14),
    },
  },

  // CONTAINERS
  MuiContainer: {
    maxWidthLg: {
      // Custom content width utilized for entirety of application.
      // All page content should be wrapped in a container with maxWidthLg as well as contextual areas of L1 / L2 Nav.
      [breakpoints.up('lg')]: {
        maxWidth: 'none',
      },
    },
  },

  // CHIPS
  MuiChip: {
    root: {
      fontSize: pxToRem(13),
      color: palette.text.primary,
      fontWeight: 400,
    },
  },

  // TOOLTIP
  MuiTooltip: {
    tooltip: {
      fontSize: pxToRem(13),
      lineHeight: pxToRem(16),
      padding: '8px',
    },
  },

  //MODAL
  MuiDialogContent: {
    root: {
      padding: pxToRem(24),
    },
  },
  MuiDialog: {
    paperWidthSm: {
      minWidth: 400,
    },
    paperWidthMd: {
      minWidth: 600,
    },
  },
  MuiDialogActions: {
    root: {
      backgroundColor: palette.brand.sky25,
    },
  },

  //TABS
  MuiTab: {
    root: {
      [breakpoints.up('sm')]: {
        minWidth: 'auto',
      },
      padding: 0,
      fontSize: pxToRem(14),
      lineHeight: '150%',
      fontWeight: 400,
      textTransform: 'none',
      letterSpacing: '0.5px',
      margin: spacing(0, 1),
      '& .Mui-selected': {
        fontWeight: 700,
      },
    },
    textColorPrimary: {
      color: palette.text.primary,
    },
    textColorInherit: {
      opacity: 1,
      '&.Mui-selected': {
        fontWeight: 600,
      },
    },
  },

  MuiTabs: {
    indicator: {
      height: '3px',
    },
  },

  MuiAlert: {
    icon: {
      marginRight: spacing(0.5),
    },
    root: {
      padding: '6px 16px',
      fontSize: pxToRem(12),
      width: '100%',
      marginTop: spacing(2),
    },
    standardSuccess: {
      backgroundColor: '#E8F5E9',
      color: '#1E4620',
      border: '1px solid #C8E6C9',
      '& $icon': {
        color: palette.success.main,
      },
    },
    standardError: {
      backgroundColor: '#F6E3E2',
      color: '#5F2120',
      border: '1px solid #E8B9B6',
      '& $icon': {
        color: palette.error.main,
      },
    },
    standardWarning: {
      backgroundColor: '#FFF3E0',
      color: '#663C00',
      border: '1px solid #FFE0B2',
      '& $icon': {
        color: palette.warning.main,
      },
    },
    standardInfo: {
      backgroundColor: '#E0EEF9',
      color: '#014361',
      border: '1px solid #B3D5EF',
      '& $icon': {
        color: palette.info.main,
      },
    },
    outlinedSuccess: {
      backgroundColor: '#E8F5E9',
      color: '#1E4620',
      border: '1px solid #C8E6C9',
      '& $icon': {
        color: palette.success.main,
      },
    },
    outlinedError: {
      backgroundColor: '#F6E3E2',
      color: '#5F2120',
      border: '1px solid #E8B9B6',
      '& $icon': {
        color: palette.error.main,
      },
    },
    outlinedWarning: {
      backgroundColor: '#FFF3E0',
      color: '#663C00',
      border: '1px solid #FFE0B2',
      '& $icon': {
        color: palette.warning.main,
      },
    },
    outlinedInfo: {
      backgroundColor: '#E0EEF9',
      color: '#014361',
      border: '1px solid #B3D5EF',
      '& $icon': {
        color: palette.info.main,
      },
    },
    filledSuccess: {
      backgroundColor: palette.success.main,
      color: palette.success.contrastText,
      '& $icon': {
        color: palette.success.contrastText,
      },
    },
    filledError: {
      backgroundColor: palette.error.main,
      color: palette.success.contrastText,
      '& $icon': {
        color: palette.success.contrastText,
      },
    },
    filledWarning: {
      backgroundColor: palette.warning.main,
      color: palette.success.contrastText,
      '& $icon': {
        color: palette.success.contrastText,
      },
    },
    filledInfo: {
      backgroundColor: palette.info.main,
      color: palette.success.contrastText,
      '& $icon': {
        color: palette.success.contrastText,
      },
    },
  },
};

export default overrides;
